import moment from "moment";

/**
 * Format a date string using moment
 * @param {string|Date} dateStr - Date to format
 * @param {string} format - Moment format string
 * @returns {string} Formatted date
 */
export const formatDate = (dateStr, format = "YYYY. M. D.") => {
  if (!dateStr) return "";
  return moment(dateStr).format(format);
};

/**
 * Format a date with time
 * @param {string|Date} dateStr - Date to format
 * @returns {string} Formatted datetime
 */
export const formatDateTime = (dateStr) => {
  return formatDate(dateStr, "YYYY-MM-DD HH:mm");
};

/**
 * Format a date as ISO date only
 * @param {string|Date} dateStr - Date to format
 * @returns {string} Formatted date (YYYY-MM-DD)
 */
export const formatDateISO = (dateStr) => {
  return formatDate(dateStr, "YYYY-MM-DD");
};

/**
 * Get remaining days until expiration
 * @param {string|Date} expDate - Expiration date
 * @returns {number} Days remaining (negative if expired)
 */
export const getRemainingDays = (expDate) => {
  if (!expDate) return 0;
  return moment(expDate).startOf("day").diff(moment().startOf("day"), "days");
};

/**
 * Get a human-readable remaining time string
 * @param {string|Date} expDate - Expiration date
 * @returns {string} "X일 남음" or "기간만료"
 */
export const getRemainingText = (expDate) => {
  const remaining = getRemainingDays(expDate);
  if (remaining > 0) {
    return `${remaining}일 남음`;
  }
  return "기간만료";
};

/**
 * Check if a date has passed
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if expired
 */
export const isExpired = (date) => {
  return getRemainingDays(date) < 0;
};

/**
 * Format phone number with dashes
 * @param {string} phone - Phone number (01012345678)
 * @returns {string} Formatted phone (010-1234-5678)
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  return phone.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
};

/**
 * Truncate an ID for display
 * @param {string|number} id - The ID to truncate
 * @param {number} length - Max length
 * @param {string} prefix - Prefix to add (e.g., "R-", "T-")
 * @returns {string} Truncated ID
 */
export const formatOrderId = (id, length = 8, prefix = "") => {
  if (!id) return "";
  return `${prefix}${String(id).slice(0, length)}`;
};
